import RNCStorage from '@react-native-async-storage/async-storage';
import {
  BehaviorSubject,
  concatMap,
  defer,
  from,
  Observable,
  Subject,
} from 'rxjs';
import { filter, mergeMap, mergeWith } from 'rxjs/operators';

export type DailyLaunchCount = {
  [key: string]: number;
};

export enum Key {
  InvalidKey = '__invalid__',

  DailyLaunchCount = 'DailyLaunchCount',
  LaunchCount = 'LaunchCount',
  IndependentLaunchCount = 'IndependentLaunchCount',
  TurnOffPurchaseAnime = 'TurnOffPurchaseAnime',
  TurnOffLikingBolt = 'TurnOffLikingBolt',
  TurnOffComments = 'TurnOffComments',

  // Broadcast settings
  AgoraAINoiseReduction = 'AgoraAINoiseReduction',
  AgoraAutomaticFaceFocus = 'AgoraAutomaticFaceFocus',

  CampaignPersistent = 'com.popshop.user.campaign_id',
  StreamingIn1080p = 'live.popshop.streaming.1080p',

  ShownGiftTutorial = 'live.popshop.giftTutorial.shown',

  RecentMentioned = 'live.popshop.recentMentions',

  LocalPurchasedSuccessCount = 'live.popshop.local.purchase.success_count',

  PlayedIntroVideo = 'live.popshop.played.introVideo',

  statsigInstallationUser = 'com.popshop.statsig.installation.user',

  ReferralGiftingDiscoveryPageSendBannerIsClosedManually = 'live.popshop.referralGiftingDiscoveryPageSendBannerIsClosedManually',
  ReferralGiftingDiscoveryPageClaimBannerIsClosedManually = 'live.popshop.referralGiftingDiscoveryPageClaimBannerIsClosedManually',

  NewOnboardingProgress = 'onboarding.v2.progress',
  NewOnboardingHasCompletion = 'onboarding.v2.has_completion',

  DiscoveryV2TopCategories = 'discovery-v2.top-categories',

  PslTopLevelProductCategories = 'psl.top-level-product-categories',

  ShopFeaturedTags = 'shop.featured-tags',

  AndroidNotificationPermissionStatus = 'android.notification.permission.status',

  AnonymousVideoFeedsSessionId = 'anonymous.video.feeds.session.id',

  InstagramImportUserConsentAgreed = 'ig_import.user_consent_agreed',

  CheckoutPaymentPending = 'checkout.payment.pending',
}

const kDefaultScope = 'live.popshop.app';

export function scopedKey(key: string) {
  return `${kDefaultScope}.${key}`;
}

namespace AsyncStorage {
  const cachedBehaviorSubjects: Partial<{
    [key: string]: BehaviorSubject<any>;
  }> = {};

  export function behaviorSubjectOrUpdateWith$<T>(
    key: string | Key,
    value: T,
    onError?: (error: Error) => T | null | undefined,
  ): BehaviorSubject<T | undefined | null> {
    if (key in cachedBehaviorSubjects) {
      const cached = cachedBehaviorSubjects[key] as any;
      cached.next(value);
      return cached;
    }
    return behaviorSubject$(key, value, onError);
  }

  export function behaviorSubject$<T>(
    key: string | Key,
    defaultValue?: T | ((key: string) => T | null | undefined),
    onError?: (error: Error) => T | null | undefined,
    skipValueLoadedFromStorage?: boolean,
  ): BehaviorSubject<T | undefined | null> {
    if (key in cachedBehaviorSubjects) {
      return cachedBehaviorSubjects[key] as any;
    }

    const storageKey = scopedKey(key);

    const initialValue =
      typeof defaultValue === 'function'
        ? (defaultValue as (key: string) => T | null | undefined)(storageKey)
        : defaultValue;

    const subject = new BehaviorSubject<T | undefined | null>(initialValue);

    cachedBehaviorSubjects[key] = subject;

    RNCStorage.getItem(storageKey).then(
      value => {
        if (skipValueLoadedFromStorage) {
          subject.next(initialValue ?? null);
        } else {
          // note(andy): only update the value if no refresh version exists yet
          // in order to prevent the current value from being overwritten in case
          // where value are updated before it is loaded from async storage.
          if (subject.value === undefined) {
            if (value != null) {
              subject.next(JSON.parse(value));
            } else {
              subject.next(initialValue ?? null);
            }
          }
        }

        void subject
          .pipe(
            concatMap(newValue =>
              from(
                newValue == null
                  ? RNCStorage.removeItem(storageKey)
                  : RNCStorage.setItem(storageKey, JSON.stringify(newValue)),
              ),
            ),
          )
          .subscribe();
      },
      e => {
        if (onError != null) {
          const value = onError(e as Error);
          subject.next(value ?? null);
        } else {
          alert((e as Error).message);
          subject.next(initialValue ?? null);
        }

        void subject
          .pipe(
            concatMap(newValue =>
              from(
                newValue == null
                  ? RNCStorage.removeItem(storageKey)
                  : RNCStorage.setItem(storageKey, JSON.stringify(newValue)),
              ),
            ),
          )
          .subscribe();
      },
    );
    return subject;
  }

  const changedKey = new Subject<Key>();

  function getDataFactory<T>(
    key: Key,
    getData: (key: Key) => Promise<T>,
  ): Observable<T> {
    const futureValue$ = changedKey.pipe(
      filter(anyKey => anyKey === key),
      mergeMap(key => getData(key)),
    );
    return defer(() => getData(key)).pipe(mergeWith(futureValue$));
  }

  export async function storeData(key: Key, value: string): Promise<void> {
    try {
      await RNCStorage.setItem(key, value);
      changedKey.next(key);
    } catch (e) {}
  }

  export async function removeData(key: Key): Promise<void> {
    try {
      await RNCStorage.removeItem(key);
      changedKey.next(key);
    } catch (e) {}
  }

  export async function getData(key: Key): Promise<string | null> {
    try {
      return await RNCStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  export function getData$(key: Key): Observable<string | null> {
    return getDataFactory(key, getData);
  }

  export async function storeJSONData(key: Key, value: any): Promise<void> {
    await AsyncStorage.storeData(key, `${JSON.stringify(value)}`);
  }

  export async function getJSONData<T>(key: Key): Promise<T | null> {
    const result = await AsyncStorage.getData(key);
    if (result == null) return result;
    try {
      return JSON.parse(result);
    } catch {
      return null;
    }
  }

  export function getJSONData$<T>(key: Key): Observable<T | null> {
    return getDataFactory<T | null>(key, getJSONData);
  }

  export async function storeNumberData(
    key: Key,
    value: number,
  ): Promise<void> {
    await AsyncStorage.storeData(key, `${value}`);
  }

  export async function getNumberData(key: Key) {
    const result = await AsyncStorage.getData(key);
    if (typeof result === 'string') {
      return +result;
    }
  }

  // TODO: store and get Object data
}

export default AsyncStorage;
